import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useDocumentStore } from '../hooks/useDocumentStore';
import { SyncStatusEvent } from '../store/createPatchRequestsBatcher';
import TextWithAnimatedEllipsis from 'components/v2/file-upload/TextWithAnimatedEllipsis';
import { toaster } from 'components/v2/toast';

const DataSyncIndicator: React.FC = () => {
  const { addDataSyncStatusListener } = useDocumentStore();
  const [status, setStatus] = React.useState<'hidden' | 'pending' | 'done' | 'error'>('hidden');

  useEffect(() => {
    let wasPreviouslySaving = false;
    // Every edit after a refused save is refused again with the same text; show it once until a save succeeds.
    let lastShownErrorMessage: string | null = null;
    let doneResetTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleStatusChange = (event: SyncStatusEvent) => {
      if (doneResetTimeout) {
        clearTimeout(doneResetTimeout);
        doneResetTimeout = null;
      }

      switch (event.type) {
        case 'flushing':
        case 'queued':
          setStatus('pending');
          wasPreviouslySaving = true;
          break;
        case 'idle':
          lastShownErrorMessage = null;
          if (wasPreviouslySaving) {
            setStatus('done');
            doneResetTimeout = setTimeout(() => {
              setStatus('hidden');
            }, 3000);
            return;
          }
          setStatus('hidden');
          break;
        case 'error':
          setStatus('error');
          if (event.message && event.message !== lastShownErrorMessage) {
            lastShownErrorMessage = event.message;
            toaster({ severity: 'error', summary: 'Error saving changes', details: event.message });
          }
          break;
      }
    };

    const removeListener = addDataSyncStatusListener(handleStatusChange);
    return () => {
      removeListener();
      if (doneResetTimeout) {
        clearTimeout(doneResetTimeout);
      }
      setStatus('hidden');
    };
  }, [addDataSyncStatusListener]);

  switch (status) {
    case 'hidden':
      return null;
    case 'pending':
      return (
        <SavingIndicator>
          <TextWithAnimatedEllipsis>Saving</TextWithAnimatedEllipsis>
        </SavingIndicator>
      );
    case 'done':
      return (
        <SavingIndicator>
          Saved
          <i className="pi pi-check" style={{ marginLeft: '8px', fontSize: 14 }}></i>
        </SavingIndicator>
      );
    case 'error':
      return (
        <SavingIndicator $error>
          Error saving changes
          <i className="pi pi-exclamation-circle" style={{ marginLeft: '8px', fontSize: 14 }}></i>
        </SavingIndicator>
      );
  }
};

export default DataSyncIndicator;

const SavingIndicator = styled.div<{ $error?: boolean }>`
  position: fixed;
  top: 110px;
  left: 32px;
  ${(props) => props.theme.typography.bodyInput};
  z-index: 10;

  ${(props) =>
    props.$error &&
    css`
      color: ${props.theme.colors.statusLabelRedText};
    `}

  opacity: 1;
  transition: opacity 0.2s ease;

  @starting-style {
    opacity: 0;
  }
`;
